export const shouldShowReceiptForStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  return ["accepted", "startduty", "endduty", "completed"].includes(normalized);
};

const toNumber = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isNurseRegularVisit = (visit) =>
  String(visit?.specialistInfo?.role || "").trim().toLowerCase() === "nurse" &&
  String(visit?.dutyType || "").trim().toLowerCase() === "regular";

const isPhysioAssessmentVisit = (visit) =>
  String(visit?.specialistInfo?.role || "").trim().toLowerCase() === "physiotherapist" &&
  visit?.isAssessment === true;

export const buildReceiptLineItems = (visit) => {
  if (isNurseRegularVisit(visit)) {
    const qty = Math.max(1, toNumber(visit?.paymentInfo?.totalDays || visit?.visitInfo?.days || 0));
    const rate = toNumber(visit?.paymentInfo?.feePerDay);
    const amount = toNumber(visit?.paymentInfo?.totalFees || qty * rate);
    return [
      {
        particulars: "HOME CARE CHARGES",
        qty,
        rate,
        amount,
      },
    ];
  }

  if (isPhysioAssessmentVisit(visit)) {
    const lines = [];
    const assessmentFee = toNumber(visit?.paymentInfo?.assessment?.fee);
    if (assessmentFee > 0) {
      lines.push({
        particulars: "Assessment Fee",
        qty: 1,
        rate: assessmentFee,
        amount: assessmentFee,
      });
    }

    const sessions = Array.isArray(visit?.paymentInfo?.sessions) ? visit.paymentInfo.sessions : [];
    sessions.forEach((session, index) => {
      const qty = Math.max(1, toNumber(session?.extendedDays || 1));
      const amount = toNumber(session?.extensionFees ?? session?.grandTotal);
      const rate = toNumber(session?.feesPerSession || (qty > 0 ? amount / qty : amount));
      if (amount > 0) {
        lines.push({
          particulars: `Session Extension ${index + 1}`,
          qty,
          rate,
          amount,
        });
      }
    });

    return lines;
  }

  const serviceFees = Array.isArray(visit?.paymentInfo?.serviceFees) ? visit.paymentInfo.serviceFees : [];
  const qty = Math.max(1, toNumber(visit?.paymentInfo?.totalDays || visit?.visitInfo?.days || 0));
  return serviceFees.map((item, index) => {
    const rate = toNumber(item?.fee ?? item?.fees);
    return {
      particulars: String(item?.service || item?.name || `Service ${index + 1}`).trim() || `Service ${index + 1}`,
      qty,
      rate,
      amount: rate * qty,
    };
  });
};

export const getReceiptTotalAmount = (visit) => {
  if (isPhysioAssessmentVisit(visit)) {
    const assessmentFee = toNumber(visit?.paymentInfo?.assessment?.fee);
    const sessions = Array.isArray(visit?.paymentInfo?.sessions) ? visit.paymentInfo.sessions : [];
    const sessionTotal = sessions.reduce(
      (sum, session) => sum + toNumber(session?.extensionFees ?? session?.grandTotal),
      0,
    );
    return assessmentFee + sessionTotal;
  }

  const totalFees = toNumber(visit?.paymentInfo?.totalFees);
  if (totalFees > 0) return totalFees;

  return buildReceiptLineItems(visit).reduce((sum, item) => sum + toNumber(item?.amount), 0);
};

const smallNumberWords = [
  "",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];

const tensWords = [
  "",
  "",
  "twenty",
  "thirty",
  "forty",
  "fifty",
  "sixty",
  "seventy",
  "eighty",
  "ninety",
];

const convertBelowThousand = (value) => {
  const num = toNumber(value);
  if (num === 0) return "";
  if (num < 20) return smallNumberWords[num];
  if (num < 100) {
    const tens = Math.floor(num / 10);
    const rest = num % 10;
    return `${tensWords[tens]}${rest ? ` ${smallNumberWords[rest]}` : ""}`.trim();
  }
  const hundreds = Math.floor(num / 100);
  const rest = num % 100;
  return `${smallNumberWords[hundreds]} hundred${rest ? ` ${convertBelowThousand(rest)}` : ""}`.trim();
};

export const numberToIndianCurrencyWords = (value) => {
  const amount = Math.round(toNumber(value));
  if (amount === 0) return "RUPEES ZERO ONLY";

  const crore = Math.floor(amount / 10000000);
  const lakh = Math.floor((amount % 10000000) / 100000);
  const thousand = Math.floor((amount % 100000) / 1000);
  const remainder = amount % 1000;
  const segments = [];

  if (crore) segments.push(`${convertBelowThousand(crore)} crore`);
  if (lakh) segments.push(`${convertBelowThousand(lakh)} lakh`);
  if (thousand) segments.push(`${convertBelowThousand(thousand)} thousand`);
  if (remainder) segments.push(convertBelowThousand(remainder));

  return `RUPEES ${segments.join(" ").trim().toUpperCase()} ONLY`;
};
